import React, { useState, useRef, useEffect, useMemo } from 'react';

interface Option {
  value: string | number;
  label: string;
  data?: any;
}

interface SingleSelectProps {
  options: Option[];
  /**
   * Max height of the open options panel, in px. Defaults to 240 (the
   * previous hard-coded `max-h-60`). Lower it when the dropdown lives in
   * a scrollable container it would otherwise overhang: the panel is
   * absolutely positioned, so an overhang grows the container's scroll
   * area and pushes the last options behind whatever sits below it.
   */
  menuMaxHeight?: number;
  value?: string | number;
  selectedLabelFallback?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  disableLocalFilter?: boolean;
  onSearchChange?: (value: string) => void;
  onLoadMore?: () => void;
  multiple?: false;
}

interface MultiSelectProps {
  options: Option[];
  /**
   * Max height of the open options panel, in px. Defaults to 240 (the
   * previous hard-coded `max-h-60`). Lower it when the dropdown lives in
   * a scrollable container it would otherwise overhang: the panel is
   * absolutely positioned, so an overhang grows the container's scroll
   * area and pushes the last options behind whatever sits below it.
   */
  menuMaxHeight?: number;
  value?: (string | number)[];
  selectedLabelFallback?: string;
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  disableLocalFilter?: boolean;
  onSearchChange?: (value: string) => void;
  onLoadMore?: () => void;
  multiple: true;
}

type SearchableDropdownProps = SingleSelectProps | MultiSelectProps;

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  selectedLabelFallback,
  onChange,
  placeholder = 'Search or select option...',
  className = '',
  disabled = false,
  error = false,
  emptyMessage = 'No options found',
  allowClear = true,
  loading = false,
  hasMore = false,
  disableLocalFilter = false,
  onSearchChange,
  onLoadMore,
  menuMaxHeight = 240,
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingMoreRef = useRef(false);

  const selectedValues = useMemo(() => {
    return multiple && Array.isArray(value) ? value : value ? [value] : [];
  }, [multiple, value]);

  const selectedOption = useMemo(() => {
    return !multiple && value
      ? options.find((option) => option?.value?.toString() === value?.toString())
      : null;
  }, [multiple, value, options]);

  useEffect(() => {
    if (multiple) {
      if (!isOpen) { setInputValue(''); setSearchValue(''); }
    } else {
      if (isOpen) return;
      if (selectedOption) setInputValue(selectedOption.label);
      else if (selectedLabelFallback && value) setInputValue(selectedLabelFallback);
      else setInputValue('');
    }
  }, [selectedValues, selectedOption, selectedLabelFallback, multiple, isOpen, value]);

  const filteredOptions = useMemo(() => {
    if (disableLocalFilter) return options;
    const searchTerm = multiple ? searchValue : inputValue;
    if (!searchTerm || (!multiple && selectedOption?.label === inputValue)) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [disableLocalFilter, inputValue, searchValue, options, selectedOption, multiple]);

  useEffect(() => { if (!loading) loadingMoreRef.current = false; }, [loading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (selectedOption) setInputValue(selectedOption.label);
        else if (selectedLabelFallback && value) setInputValue(selectedLabelFallback);
        else setInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption, selectedLabelFallback, value]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && inputRef.current) inputRef.current.focus();
    }
  };

  const handleOptionSelect = (option: Option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(option.value);
      const newValues = isSelected
        ? currentValues.filter((v) => v !== option.value)
        : [...currentValues, option.value];
      (onChange as (value: (string | number)[]) => void)(newValues);
    } else {
      (onChange as (value: string) => void)(option.value as string);
      setInputValue(option.label);
      setIsOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) (onChange as (value: (string | number)[]) => void)([]);
    else (onChange as (value: string) => void)('');
    setIsOpen(false);
  };

  const handleRemoveTag = (valueToRemove: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple && Array.isArray(value)) {
      (onChange as (value: (string | number)[]) => void)(value.filter((v) => v !== valueToRemove));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (multiple) { setSearchValue(val); setInputValue(val); }
    else setInputValue(val);
    onSearchChange?.(val);
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIsOpen(true); }
    else if (e.key === 'Escape') {
      setIsOpen(false);
      if (selectedOption) setInputValue(selectedOption.label);
      else setInputValue('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) handleOptionSelect(filteredOptions[0]);
      else if (filteredOptions.length > 1) {
        const exactMatch = filteredOptions.find(
          (option) => option.label.toLowerCase() === inputValue.toLowerCase()
        );
        if (exactMatch) handleOptionSelect(exactMatch);
      }
    }
  };

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loading || loadingMoreRef.current) return;
    const target = e.currentTarget;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
    if (isNearBottom) { loadingMoreRef.current = true; onLoadMore(); }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        {multiple ? (
          <div
            className={`
              w-full min-h-10 px-3 py-1 border rounded-md bg-bg-white-0
              focus-within:outline-hidden focus-within:ring-2 focus-within:ring-primary transition-colors
              ${error ? 'border-error-base' : 'border-stroke-sub-300'}
              ${disabled ? 'bg-bg-soft-200 cursor-not-allowed' : 'hover:border-stroke-strong-950'}
              ${isOpen ? 'ring-2 ring-primary border-primary' : ''}
              flex flex-wrap items-center gap-1
            `}
          >
            {selectedValues.map((val, index) => {
              const displayVal = Array.isArray(val) ? val[0] : val;
              const option = options.find((opt) => opt.value === displayVal);
              const label = option?.label || displayVal?.toString() || '';
              return (
                <span
                  key={`${displayVal}-${index}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-feature-lighter text-primary border border-feature-light"
                >
                  {label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemoveTag(displayVal as string | number, e)}
                      className="ml-1 text-primary hover:text-primary-dark focus:outline-hidden"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  )}
                </span>
              );
            })}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              disabled={disabled}
              className="flex-1 min-w-[120px] outline-hidden bg-transparent text-sm text-text-strong-950 placeholder:text-text-soft-400"
              style={{ minWidth: selectedValues.length > 0 ? '120px' : 'auto' }}
            />
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              w-full px-3 py-2 pr-10 border rounded-md text-sm bg-bg-white-0
              text-text-strong-950 placeholder:text-text-soft-400
              focus:outline-hidden focus:ring-2 focus:ring-primary transition-colors
              ${error ? 'border-error-base' : 'border-stroke-sub-300'}
              ${disabled ? 'bg-bg-soft-200 cursor-not-allowed' : 'hover:border-stroke-strong-950'}
              ${isOpen ? 'ring-2 ring-primary border-primary' : ''}
            `}
          />
        )}

        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
          {allowClear && (multiple ? selectedValues.length > 0 : selectedOption) && !disabled && (
            <button
              onClick={handleClear}
              className="text-icon-soft-400 hover:text-text-sub-600 transition-colors"
              type="button"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
          <button
            onClick={handleToggle}
            className="text-icon-soft-400 hover:text-text-sub-600 transition-colors"
            type="button"
          >
            <i className={`ri-arrow-down-s-line transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-bg-white-0 border border-stroke-soft-200 rounded-md shadow-card-sm overflow-y-auto"
          style={{ maxHeight: menuMaxHeight }}
          onScroll={handleDropdownScroll}
        >
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.map((option) => {
                const isSelected = multiple
                  ? selectedValues.includes(option.value)
                  : selectedOption?.value === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    className={`
                      w-full px-3 py-2 text-left text-sm hover:bg-bg-weak-50 transition-colors cursor-pointer flex items-center
                      ${isSelected ? 'bg-feature-lighter text-primary font-medium' : 'text-text-strong-950'}
                    `}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="mr-2 text-primary focus:ring-primary"
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                );
              })}
              {loading && (
                <div className="px-3 py-2 text-sm text-text-sub-600 text-center">Loading...</div>
              )}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-text-sub-600 text-center">
              {loading ? 'Loading...' : emptyMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
